import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronRight, Loader, X } from "lucide-react";
// import FieldOfWork from "components/Reusable/FieldOfWork";
import { FaCheckSquare } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { useAlert } from "../../../components/UI_components/Alert";
import FieldOfWork from "components/Reusable/FieldOfWork";
import ModalPortal from "components/ModalPortal";

const UnifiedForm = ({ type, onClose, asSidebar = false }) => {
  const alert = useAlert();
  const [showFieldOfWork, setShowFieldOfWork] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    fieldOfWork: "",
    workname_id: null,
    other_workname: null,
    address: "",
    paymentType: type === "contractor" ? "Contract" : "Daily wage",
    amount: "",
  });

  // ── Phone state: raw 10-digit input + real-time error ──
  const [contactNumber, setContactNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const isContractor = type === "contractor";
  const title = isContractor ? "New Contractor" : "New Worker";
  const nameLabel = isContractor ? "Leader's Name" : "Worker's Name";
  const contactLabel = isContractor
    ? "Leader's Contact number"
    : "Worker's Contact number";
  const isFormValid =
    Boolean(formData.name.trim()) &&
    Boolean(formData.workname_id || formData.other_workname) &&
    Boolean(formData.amount && Number(formData.amount) > 0) &&
    (contactNumber.length === 0 || contactNumber.length === 10);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setIsMounted(true));

    return () => {
      window.cancelAnimationFrame(raf);
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const requestClose = (afterClose) => {
    if (isClosing) return;
    setIsClosing(true);
    setIsMounted(false);
    closeTimerRef.current = window.setTimeout(() => {
      if (afterClose) {
        afterClose();
        return;
      }
      onClose?.();
    }, 260);
  };

  // ── Strips non-digits, caps at 10, validates length in real time ──
  const handlePhoneInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setContactNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Valid 10-digit contact number is required");
    } else {
      setPhoneError("");
    }
    // Clear the step-level contact error too (set by validateStep1 on submit)
    if (errors.contact) {
      setErrors((prev) => ({ ...prev, contact: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.workname_id && !formData.other_workname) {
      newErrors.fieldOfWork = "Field of work is required";
    }
    // Phone is validated separately via phoneError / contactNumber state
    // if (contactNumber.length !== 10) {
    //   setPhoneError("Valid 10-digit contact number is required");
    //   newErrors.contact = true; // sentinel to block submit
    // }
    if (contactNumber.length > 0 && contactNumber.length !== 10) {
      setPhoneError("Valid 10-digit contact number is required");
      newErrors.contact = true;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      newErrors.amount = "Valid amount is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldSelect = (field) => {
    setFormData((prev) => ({
      ...prev,
      fieldOfWork: field.name,
      workname_id: field.id,
      other_workname: field.other_workname ?? null,
    }));
    if (errors.fieldOfWork) {
      setErrors((prev) => ({ ...prev, fieldOfWork: undefined }));
    }
    setShowFieldOfWork(false);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const createWorker = async () => {
    try {
      const token = localStorage.getItem("access");
      const site = localStorage.getItem("site");
      const response = await axios.post(
        `${BASE_URL}/worker/create-worker/`,
        {
          types: isContractor ? "Crew" : "Worker",
          name: formData.name,
          contact: contactNumber ? "+91" + contactNumber : null,
          address: formData.address,
          sites: parseInt(site),
          workname_id: formData.workname_id,
          other_workname: formData.other_workname,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.status) {
        return response.data.data.id;
      } else {
        alert.addAlert(response.data.message, "error");
      }
      return null;
    } catch (error) {
      console.error("Failed to create worker:", error);
      setErrors({ submit: "Failed to create worker. Please try again." });
      return null;
    }
  };

  const createPayment = async (createdWorkerId) => {
    try {
      const token = localStorage.getItem("access");
      const response = await axios.post(
        `${BASE_URL}/worker/create-workerpayment/`,
        {
          payment_type: formData.paymentType,
          amount: parseFloat(formData.amount),
          worker_id: createdWorkerId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data.status;
    } catch (error) {
      setErrors({
        submit: "Failed to create payment details. Please try again.",
      });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaveClicked) return;
    setIsSaveClicked(true);
    if (!validateForm()) {
      setIsSaveClicked(false);
      return;
    }

    setIsLoading(true);
    const createdWorkerId = await createWorker();
    if (!createdWorkerId) {
      setIsLoading(false);
      setIsSaveClicked(false);
      return;
    }

    const paymentSuccess = await createPayment(createdWorkerId);
    setIsLoading(false);
    if (paymentSuccess) {
      alert.addAlert("Worker created successfully", "success");
      requestClose();
    } else {
      setIsSaveClicked(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${
        asSidebar ? "flex items-stretch justify-end" : "flex items-center justify-center"
      } ${isMounted && !isClosing ? "opacity-100" : "opacity-0"}`}
      onClick={() => requestClose()}
    >
      <div
        className={`shadow-2xl relative overflow-hidden bg-white transition-all duration-300 ease-out ${
          asSidebar
            ? `w-full sm:max-w-xl h-full rounded-l-xl flex flex-col ${
                isMounted && !isClosing
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              }`
            : `w-full max-w-2xl mx-auto rounded-2xl ${
                isMounted && !isClosing
                  ? "scale-100 opacity-100"
                  : "scale-95 opacity-0"
              }`
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="sticky top-0 z-10 flex h-16 items-center justify-center border-b border-gray-100 bg-white px-4 py-2">
            <div className="w-8" />
            <h3 className="text-xl font-medium text-[#232323]">{title}</h3>
            <button
              onClick={() => requestClose()}
              className="absolute right-4 flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 pb-6">
            {errors.submit && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {errors.submit}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-base font-semibold text-[#232323]">
                  Worker Details
                </h3>
                <p className="mb-1 text-[13px] text-gray-600">
                  Add name, field of work, contact, address and payment details
                </p>
              </div>

              <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-4">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-medium text-[#232323]"
                  >
                    {nameLabel}
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Type"
                    className={`block w-full rounded-lg bg-[#F3F4F6] px-3 py-2.5 text-sm ${errors.name ? "border border-red-500" : "border border-transparent focus:border-[#3B0069]"}`}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="fieldOfWork"
                    className="mb-2 block text-sm font-medium text-[#232323]"
                  >
                    Field of Work
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFieldOfWork(true)}
                    className={`flex w-full items-center justify-between rounded-xl bg-[#F3F4F6] px-4 py-2.5 text-left text-sm ${errors.fieldOfWork ? "border border-red-500" : "border border-transparent hover:border-gray-300"}`}
                  >
                    <span className="text-left">
                      {formData.fieldOfWork || "Select Field of Work"}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  </button>
                  {errors.fieldOfWork && (
                    <p className="text-sm text-red-500">{errors.fieldOfWork}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="contact"
                    className="mb-2 block text-sm font-medium text-[#232323]"
                  >
                    {contactLabel}
                    (Optional)
                  </label>
                  <div
                    className={`flex items-center rounded-xl bg-[#F3F4F6] px-4 py-2 transition-colors ${
                      phoneError ? "border border-red-500" : "border border-gray-200"
                    }`}
                  >
                    <span className="mr-2 select-none text-gray-600">+91</span>
                    <input
                      id="contact"
                      type="tel"
                      maxLength={10}
                      value={contactNumber}
                      onInput={handlePhoneInput}
                      placeholder="98700 00000"
                      className="w-full bg-transparent text-sm text-[#232323] outline-none placeholder:text-gray-400"
                    />
                  </div>
                  {phoneError && (
                    <p className="text-sm text-red-500">{phoneError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="mb-2 block text-sm font-medium text-[#232323]">
                    Payment Type
                  </label>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-[#3B0069] bg-[#ECE6F0] px-4 py-2.5 text-sm text-gray-900"
                  >
                    {formData.paymentType}
                    <FaCheckSquare className="text-text-clr" />
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="mb-2 block text-sm font-medium text-[#232323]">
                    Amount
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      ₹
                    </span>
                    <input
                      type="number"
                      id="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="Amount"
                      className={`block w-full rounded-lg bg-[#F3F4F6] py-2.5 pl-8 pr-4 text-sm ${errors.amount ? "border border-red-500" : "border border-transparent focus:border-[#3B0069]"}`}
                    />
                  </div>
                  {errors.amount && (
                    <p className="text-sm text-red-500">{errors.amount}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="address"
                    className="mb-2 block text-sm font-medium text-[#232323]"
                  >
                    Address (Optional)
                  </label>
                  <input
                    id="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Type"
                    className="block w-full rounded-lg bg-[#F3F4F6] px-3 py-2.5 text-sm"
                  />
                </div>
              </div>
            </form>
          </div>

          <div className="flex shrink-0 items-center justify-center border-t border-gray-100 bg-white px-6 py-4">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || isSaveClicked || !isFormValid}
              className={`flex min-w-[240px] items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[#3B0069] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[#3B0069D9] disabled:cursor-not-allowed ${
                isLoading || isSaveClicked || !isFormValid ? "opacity-40" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <Loader className="h-5 w-5 animate-spin" /> Processing...
                </>
              ) : (
                <>
                  Create
                  <span className="flex items-center justify-center rounded-full bg-[#58267F] p-1">
                    <Check size={18} />
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Field of Work Modal */}
        {showFieldOfWork && (
          <ModalPortal>
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
              <div
                className="flex w-full max-w-xl max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
                  <div className="w-8" />
                  <h2 className="text-xl font-medium text-[#232323]">
                    Field of Work
                  </h2>
                  <div className="w-8">
                    <button
                      onClick={() => setShowFieldOfWork(false)}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-3">
                  <FieldOfWork
                    onSelect={handleFieldSelect}
                    onClose={() => setShowFieldOfWork(false)}
                  />
                </div>
              </div>
            </div>
          </ModalPortal>
        )}
      </div>
    </div>
  );
};

export default UnifiedForm;
